import React from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  CurrencyDollar,
  ChartLine,
  Plus,
  DotsSixVertical,
} from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ClientTable from '../components/ClientTable';
import ForecastChart from '../components/ForecastChart';
import AIInsights from '../components/BoardAIInsights';
import ClientDrillDown from '../components/ClientDrillDown';
import BulkSalesEntry from '../components/BulkSalesEntry';
import ClientHealthWidget from '../components/ClientHealthWidget';
import ImportHistory from '../components/ImportHistory';
import { BACKEND_URL } from '../config';

const SortableWidget = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  if (!children) return null;
  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`widget-${id}`}>
      <div
        {...attributes}
        {...listeners}
        className="absolute -left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10 bg-card border border-border rounded-sm p-1"
        title="Drag to reorder"
      >
        <DotsSixVertical size={14} weight="bold" className="text-muted-foreground" />
      </div>
      {children}
    </div>
  );
};

const OverviewTab = ({
  analytics,
  clients,
  allClientNames,
  forecast,
  widgets,
  widgetOrder,
  setWidgetOrder,
  privacyMode,
  maskCurrency,
  canWrite,
  onDataRefresh,
  drilldownClientId,
  setDrilldownClientId,
  selectedClientIds,
  setSelectedClientIds,
  onBulkDeleteOpen,
  onEditClient,
  onDeleteClient,
  clientTotal,
  clientTotalPages,
  clientPage,
  onPageChange,
  onSearchChange,
  onSortChange,
  addClientOpen,
  setAddClientOpen,
  addSaleOpen,
  setAddSaleOpen,
  newClient,
  setNewClient,
  newSale,
  setNewSale,
  onAddClient,
  onAddSale,
  bulkSaleOpen,
  setBulkSaleOpen,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = widgetOrder.indexOf(active.id);
    const newIndex = widgetOrder.indexOf(over.id);
    const newOrder = arrayMove(widgetOrder, oldIndex, newIndex);
    setWidgetOrder(newOrder);
    try {
      const axios = (await import('axios')).default;
      await axios.patch(`${BACKEND_URL}/api/user/widget-order`, { widget_order: newOrder }, { withCredentials: true });
    } catch (err) { console.error(err.message); }
  };

  if (drilldownClientId) {
    return <ClientDrillDown clientId={drilldownClientId} onBack={() => setDrilldownClientId(null)} />;
  }

  return (
    <>
      <AIInsights board="sales" />
      <ImportHistory type="sales" onImportDeleted={onDataRefresh} />

      <ClientHealthWidget />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={widgetOrder} strategy={verticalListSortingStrategy}>
          {widgetOrder.map((wid) => {
            if (wid === 'revenueChart_quickActions' && (widgets.revenueChart || widgets.quickActions)) return (
              <SortableWidget key={wid} id={wid}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
                {widgets.revenueChart && (
                <div className="lg:col-span-2 bg-card border border-border rounded-sm p-6 h-full">
                  <div className="flex items-center mb-6">
                    <ChartLine size={24} weight="regular" className="mr-3" />
                    <div>
                      <div className="text-xl font-semibold tracking-tight">Revenue Trend</div>
                      <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">FINANCIAL YEAR (APR-MAR)</div>
                    </div>
                  </div>
                  {privacyMode ? (
                    <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">Chart hidden in privacy mode</div>
                  ) : analytics?.chart_data?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={analytics.chart_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" stroke="hsl(var(--foreground))" style={{ fontSize: '12px' }} />
                        <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '12px' }} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem' }} />
                        <Line type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ fill: 'hsl(var(--accent))', r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">No sales data yet</div>
                  )}
                </div>
                )}
                {widgets.quickActions && (
                <div className="bg-card border border-border rounded-sm p-6 h-full">
                  <div className="text-xl font-semibold tracking-tight mb-6">Quick Actions</div>
                  <div className="space-y-3">
                    {canWrite ? (
                      <>
                        <Dialog open={addClientOpen} onOpenChange={setAddClientOpen}>
                          <DialogTrigger asChild>
                            <Button data-testid="add-client-button" className="w-full rounded-sm justify-start" variant="outline">
                              <Plus size={18} weight="bold" className="mr-2" />
                              Add New Client
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-2xl tracking-tight">Add New Client</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={onAddClient} className="space-y-4">
                              {['name', 'email', 'company', 'industry', 'region'].map((field) => (
                                <div key={field} className="space-y-2">
                                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
                                    {field === 'name' ? 'Client Name *' : field.charAt(0).toUpperCase() + field.slice(1)}
                                  </Label>
                                  <Input
                                    data-testid={`client-${field}-input`}
                                    type={field === 'email' ? 'email' : 'text'}
                                    value={newClient[field]}
                                    onChange={(e) => setNewClient({ ...newClient, [field]: e.target.value })}
                                    className="rounded-sm"
                                    required={field === 'name'}
                                    placeholder={field === 'industry' ? 'e.g., Technology, Healthcare, Finance' : field === 'region' ? 'e.g., North America, Europe, Asia' : undefined}
                                  />
                                </div>
                              ))}
                              <Button data-testid="submit-client-button" type="submit" className="w-full rounded-sm">Add Client</Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                        <Dialog open={addSaleOpen} onOpenChange={setAddSaleOpen}>
                          <DialogTrigger asChild>
                            <Button data-testid="record-sale-button" className="w-full rounded-sm justify-start" variant="outline">
                              <CurrencyDollar size={18} weight="bold" className="mr-2" />
                              Record Sale
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-sm">
                            <DialogHeader>
                              <DialogTitle className="text-2xl tracking-tight">Record Sale</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={onAddSale} className="space-y-4">
                              <div className="space-y-2">
                                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Client *</Label>
                                <select data-testid="sale-client-select" value={newSale.client_id} onChange={(e) => setNewSale({ ...newSale, client_id: e.target.value })} className="w-full h-10 px-3 rounded-sm border border-input bg-background" required>
                                  <option value="">Select a client</option>
                                  {allClientNames.map((client) => (<option key={client.id} value={client.id}>{client.name}</option>))}
                                </select>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Amount *</Label>
                                <Input data-testid="sale-amount-input" type="number" step="0.01" value={newSale.amount} onChange={(e) => setNewSale({ ...newSale, amount: e.target.value })} className="rounded-sm" required />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Description</Label>
                                <Input data-testid="sale-description-input" value={newSale.description} onChange={(e) => setNewSale({ ...newSale, description: e.target.value })} className="rounded-sm" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Date</Label>
                                <Input data-testid="sale-date-input" type="date" value={newSale.date} onChange={(e) => setNewSale({ ...newSale, date: e.target.value })} className="rounded-sm" />
                                <p className="text-[10px] text-muted-foreground">Leave blank to use today's date</p>
                              </div>
                              <Button data-testid="submit-sale-button" type="submit" className="w-full rounded-sm">Record Sale</Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                        <Button data-testid="bulk-sale-button" className="w-full rounded-sm justify-start" variant="outline" onClick={() => setBulkSaleOpen(true)}>
                          <Plus size={18} weight="bold" className="mr-2" />
                          Bulk Sales Entry
                        </Button>
                        <BulkSalesEntry open={bulkSaleOpen} onOpenChange={setBulkSaleOpen} allClientNames={allClientNames} onSuccess={onDataRefresh} />
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground py-4 text-center">
                        You have read-only access. Contact an admin to get write permissions.
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
              </SortableWidget>
            );
            if (wid === 'forecast' && widgets.forecast) return (
              <SortableWidget key={wid} id={wid}><ForecastChart forecast={forecast} /></SortableWidget>
            );
            if (wid === 'clientTable' && widgets.clientTable) return (
              <SortableWidget key={wid} id={wid}>
                <ClientTable
                  clients={clients}
                  canWrite={canWrite}
                  selectedClientIds={selectedClientIds}
                  setSelectedClientIds={setSelectedClientIds}
                  onBulkDeleteOpen={onBulkDeleteOpen}
                  onEditClient={onEditClient}
                  onDeleteClient={onDeleteClient}
                  onClientClick={(id) => setDrilldownClientId(id)}
                  totalClients={clientTotal}
                  totalPages={clientTotalPages}
                  serverPage={clientPage}
                  onPageChange={onPageChange}
                  onSearchChange={onSearchChange}
                  onSortChange={onSortChange}
                />
              </SortableWidget>
            );
            return null;
          })}
        </SortableContext>
      </DndContext>
    </>
  );
};

export default OverviewTab;
